"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.raiseOvertimeRequestSchema = exports.overtimeRequestDetailSchema = exports.revokeOvertimeRequestSchema = exports.overtimeRequestSchema = exports.updateOvertimeTypeRequestSchema = exports.createOvertimeTypeRequestSchema = exports.overtimeTypeSchema = exports.OVERTIME_SETTLEMENTS = exports.OVERTIME_APPLIES_WHEN = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const leave_request_1 = require("./leave-request");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Overtime — C48, Phase 3.                                                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A CLAIM ABOUT AN HOUR SOMEBODY ALREADY LOGGED, NEVER A FORM OF ITS OWN.** The mockup's
 * empty state is the specification: *"túlórát az időrögzítésnél jelölsz — a bejegyzésből
 * automatikusan kérelem lesz"*. Every shape here therefore points at a worklog, and the hours are
 * the worklog's rather than a copy.
 *
 * ⚠️ **The statuses are leave's**, deliberately: the approvals queue holds both kinds in one list,
 * and two enums meaning `pending` would be two things to render, filter and translate.
 */
exports.OVERTIME_APPLIES_WHEN = ['any', 'weekend', 'holiday'];
/**
 * ┌────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT AN APPROVED CLAIM BECOMES — feedback round 4 item 31.                 │
 * └────────────────────────────────────────────────────────────────────┘
 *
 * `payout` is what approval always did: the multiplier freezes and the hours land in project cost.
 * `credit` is the new half — **no multiplier, because nothing is being priced** — and the hours
 * raise a leave quota instead. `0146` holds the rule as a check constraint, per branch.
 */
exports.OVERTIME_SETTLEMENTS = ['payout', 'credit'];
/**
 * ⚠️⚠️ **NO `code` SINCE `0145` — feedback round 4 item 29.** `0111` gave every type one,
 * modelled on the LEAVE type's code, which is a different thing: a leave code reaches a payroll
 * export, so somebody outside this product reads it. Nobody ever read an overtime type's. It was
 * a required field with a format nobody could guess, standing between an administrator and the two
 * facts that matter — the name and the multiplier — and `id` was already the internal key it was
 * pretending to be.
 *
 * ⚠️ Not to be confused with `overtimeRequestSchema.code` below, which is `OT-2001`: a REQUEST's
 * human reference, quoted between people, and staying exactly as it is.
 */
exports.overtimeTypeSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `overtime_type_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('overtimeType'),
    name: zod_1.z.string(),
    /** ⚠️ A string: `numeric` crosses the driver as one, and 1.5 × 3 must not be a float. */
    multiplier: zod_1.z.string(),
    appliesWhen: zod_1.z.enum(exports.OVERTIME_APPLIES_WHEN),
    active: zod_1.z.boolean(),
    /** ⚠️ Exactly one per workspace — a partial unique index says so. */
    isDefault: zod_1.z.boolean(),
    sortOrder: zod_1.z.number().int(),
    /**
     * ⚠️ How many claims point at it — what makes deactivating rather than deleting legible, and the
     * same job `quotaCount` does for a leave type and `assignedCount` for a schedule template. The
     * settings row says it before the delete is offered, so a refusal is never a surprise.
     */
    claimCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
exports.createOvertimeTypeRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    multiplier: zod_1.z.number().min(0.01).max(10),
    appliesWhen: zod_1.z.enum(exports.OVERTIME_APPLIES_WHEN),
    active: zod_1.z.boolean().optional(),
    isDefault: zod_1.z.boolean().optional(),
});
exports.updateOvertimeTypeRequestSchema = exports.createOvertimeTypeRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
exports.overtimeRequestSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `overtime_request_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('overtimeRequest'),
    /** ⚠️ `OT-2001` — the leave request's `code` argument, second series. `0114`. */
    code: zod_1.z.string(),
    worklogId: zod_1.z.string(),
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from.** The normalised email,
     * produced by `wireAvatarSeed`. This shape carries no avatar URL, which is why the first
     * pass missed it — it went looking for pictures and this row only ever had initials.
     */
    memberAvatarSeed: zod_1.z.string().nullable(),
    /** 28 Oct 2026 · their uploaded picture's path, when they have one — drawn over the seed's face. */
    memberAvatarUrl: zod_1.z.string().nullable(),
    /** What the hour was logged against — the row a reader clicks to see what was worked on. */
    taskKey: zod_1.z.string().nullable(),
    workedOn: zod_1.z.string(),
    /** ⚠️ The WORKLOG's hours, read through the join. Correcting the log corrects the claim. */
    hours: zod_1.z.string(),
    /**
     * ┌────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ WHICH KIND OF OVERTIME — AND THE APPROVER DECIDES, SO IT IS NULLABLE.       │
     * └────────────────────────────────────────────────────────────────────┘
     *
     * A person marking an hour says *this is overtime* and nothing more; **which** kind it is, and
     * therefore what it is worth, is a decision about money and belongs to whoever approves it
     * (`0147`, the product owner's ruling of 20 Sep). Null until then.
     */
    overtimeTypeId: zod_1.z.string().nullable(),
    overtimeTypeName: zod_1.z.string().nullable(),
    status: zod_1.z.enum(leave_request_1.LEAVE_REQUEST_STATUSES),
    /**
     * ⚠️⚠️ **NULL UNTIL THE CLAIM IS PRICED, AND THAT IS THE POINT.** It used to fall back to the
     * TYPE's current multiplier while pending — so a queue row read *Függőben · 2×*, a rate beside a
     * claim nobody had decided. P3-5's freeze still holds for the other direction: once approved this
     * is what was agreed, and re-pricing the type re-prices nothing.
     *
     * ⚠️ Null on a CREDIT too — nothing is priced when the hours become leave.
     */
    multiplier: zod_1.z.string().nullable(),
    /** ⚠️ `hours × multiplier`, and null wherever the multiplier is. The *elszámolt óra* total. */
    accountedHours: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **HOW IT WAS SETTLED, AND `null` UNTIL IT IS** — item 31. Pending and rejected claims
     * settle nothing; an approved one is `payout` (see `multiplier` above) or `credit`.
     */
    settlement: zod_1.z.enum(exports.OVERTIME_SETTLEMENTS).nullable(),
    /** ⚠️ The leave type a credit landed in, and its name for the row. Both null on a payout. */
    creditLeaveTypeId: zod_1.z.string().nullable(),
    creditLeaveTypeName: zod_1.z.string().nullable(),
    /**
     * ⚠️ **A STRING, AND FRACTIONAL** — item 25: the balance accrues in parts of a day and only the
     * takeable figure is rounded down. `numeric` crosses the driver as a string, and 1.5 + 1.5 must
     * not be a float.
     */
    creditedDays: zod_1.z.string().nullable(),
    approverName: zod_1.z.string().nullable(),
    /** 27 Oct 2026 · the approver's face — the picture path, else the seed — so the column draws it. */
    approverAvatarUrl: zod_1.z.string().nullable(),
    approverAvatarSeed: zod_1.z.string().nullable(),
    /** When the claim was raised — the list's *Beadva* column, as on a leave request. */
    createdAt: zod_1.z.string(),
    decidedByName: zod_1.z.string().nullable(),
    decidedAt: zod_1.z.string().nullable(),
    canDecide: zod_1.z.boolean(),
    /**
     * Whether the caller may withdraw it — the claimant, while it is still pending. An approved
     * claim has been paid or credited, and taking that back is a decider's correction, not this.
     */
    canRevoke: zod_1.z.boolean(),
    version: zod_1.z.number().int(),
});
/** `POST /overtime/:id/revoke` — the version the claimant was looking at. */
exports.revokeOvertimeRequestSchema = zod_1.z.object({ version: zod_1.z.number().int() });
/**
 * One claim, in full — the other half of the mockup's request detail.
 *
 * ⚠️⚠️ **A CLAIM HAS NO THREAD AND NO ATTACHMENT, AND THAT IS THE SCHEMA SPEAKING.** `0113` gave
 * the comment table a foreign key to `leave_requests`; an overtime claim is a fact about one logged
 * hour, and the conversation about it belongs on the worklog. So this detail carries the hour's own
 * context — the task, the note on the log — rather than an empty thread the screen would draw.
 */
exports.overtimeRequestDetailSchema = exports.overtimeRequestSchema.extend({
    /** The claim's own note, written when it was raised. */
    note: zod_1.z.string().nullable(),
    /** The worklog's note, which is what the hour was actually for. */
    worklogNote: zod_1.z.string().nullable(),
    taskTitle: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    decisionNote: zod_1.z.string().nullable(),
});
/**
 * ⚠️⚠️ **THE ONLY WAY TO RAISE ONE: NAME A WORKLOG.** There is no date, no hours and no person in
 * this shape, because all three are facts about the hour being claimed. A type may be named; absent
 * means *"whatever the calendar says this day is"*, which is what the marking control offers.
 */
/**
 * ⚠️⚠️ **RAISING A CLAIM NAMES NO TYPE — `0147`.** It used to accept one, which let a requester
 * choose their own rate. What a person asserts is that the hour was overtime; the rest is the
 * approver's.
 */
exports.raiseOvertimeRequestSchema = zod_1.z.object({
    worklogId: zod_1.z.string(),
    note: zod_1.z.string().max(2000).nullable().optional(),
});
//# sourceMappingURL=overtime.js.map